import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Pressable,
  Image,
  ActivityIndicator,
  Dimensions,
  Platform,
  Text as RNText,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import { Text, Theme, Button, MarkdownRenderer, InstallAppPrompt } from '../../components/ui';
import { ReaderSegmentsService, ReaderSegment, ReaderData } from '../../services/readerSegmentsService';
import { FireInsight } from '../../models/FirestoreModels';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import FullAudioPlayer from '../../components/AudioPlayer/FullAudioPlayer';
import { Ionicons } from '@expo/vector-icons';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import ReadingSettingsSheet from '../../components/Reader/ReadingSettingsSheet';
import { ReadingSettings, getReadingSettings } from '../../utils/readingSettings';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import { styles } from './BookReaderScreen.styles';
import { useSubscription } from '../../hooks/useSubscription';
import { DailyFreeBookService } from '../../services/dailyFreeBookService';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface RouteParams {
  bookId?: string;
  languageCode?: string;
  includeIntroduction?: boolean;
  insightIds?: string[];
  readerData?: ReaderData;
  insights?: FireInsight[];
  isMixedInsights?: boolean;
  book?: any;
  currentIndex?: number;
  fromRoute?: string;
  autoPlayAudio?: boolean;
}

const BookReaderScreen: React.FC = () => {
  useNavigationLogging('BookReaderScreen');
  
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route = useRoute();
  const params = route.params as RouteParams;
  
  const [readerData, setReaderData] = useState<ReaderData | null>(params.readerData || null);
  const [currentIndex, setCurrentIndex] = useState(() => {
    const index = params.currentIndex ?? 0;
    console.log('📍 BookReaderScreen initializing with currentIndex:', index);
    if (params.readerData && params.readerData.segments) {
      const clampedIndex = Math.max(0, Math.min(index, params.readerData.segments.length - 1));
      console.log('📍 Clamped to:', clampedIndex, 'of', params.readerData.segments.length, 'segments');
      return clampedIndex;
    }
    return Math.max(0, index);
  });
  const [isLoading, setIsLoading] = useState(!params.readerData);
  const [hasLoadedAudio, setHasLoadedAudio] = useState(false);
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);
  const [readingSettings, setReadingSettings] = useState<ReadingSettings | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  
  const flatListRef = useRef<ScrollView>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  
  const { 
    isFullPlayerVisible,
    currentIndex: audioCurrentIndex,
    isPlaying, 
    playPause,
    jumpToSegment,
    playlist,
    loadBookAudio,
    loadInsightsOnly,
    loadMixedInsights,
    showFullPlayer,
  } = useAudioPlayer();
  const { hasContentAccess, isLoading: isSubscriptionLoading } = useSubscription();
  const [isFreeToday, setIsFreeToday] = useState(false);
  const [checkingFree, setCheckingFree] = useState(true);
  
  // Set initial page position
  useEffect(() => {
    if (readerData && currentIndex > 0) {
      console.log('📍 Setting initial position to page', currentIndex);
      setTimeout(() => {
        flatListRef.current?.scrollTo({ x: currentIndex * SCREEN_WIDTH, y: 0, animated: false });
      }, 100);
    }
  }, [readerData]);
  
  // Load reader data
  useEffect(() => {
    if (readerData) return;
    
    const loadData = async () => {
      setIsLoading(true);
      try {
        let data: ReaderData;
        
        if (params.bookId) {
          data = await ReaderSegmentsService.buildBookSegments(
            params.bookId,
            params.languageCode || 'en',
            params.includeIntroduction
          );
        } else if (params.insightIds && params.insightIds.length > 0) {
          data = await ReaderSegmentsService.buildCollectionSegments(
            params.insightIds,
            params.languageCode || 'en'
          );
        } else if (params.insights && params.insights.length > 0) {
          const includeIntro = params.book && !!params.book.introduction && !params.isMixedInsights;
          data = await ReaderSegmentsService.buildSegmentsFromInsights(
            params.insights,
            params.book,
            includeIntro
          );
        } else if (params.book) {
          const bookIdWithLang = typeof params.book === 'string' ? params.book : params.book.id;
          const parts = bookIdWithLang.split('_');
          const bookId = parts.length > 1 ? parts[0] : bookIdWithLang;
          const langCode = parts.length > 1 ? parts[1] : 'en';
          data = await ReaderSegmentsService.buildBookSegments(bookId, langCode, true);
        } else {
          throw new Error('No valid data source provided to BookReaderScreen');
        }
        
        setReaderData(data);
        setCurrentIndex(prevIndex => Math.max(0, Math.min(prevIndex, data.segments.length - 1)));
      } catch (error) {
        console.error('Error loading reader data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Check if current book is today's free book
  useEffect(() => {
    let mounted = true;
    const checkFree = async () => {
      const bookIdCandidate =
        params.bookId ||
        (typeof params.book === 'string' ? params.book : params.book?.id) ||
        readerData?.book?.id;
      if (!bookIdCandidate) {
        if (mounted) setCheckingFree(false);
        return;
      }
      try {
        const isFree = await DailyFreeBookService.isBookFreeToday(
          bookIdCandidate,
          params.languageCode || 'en'
        );
        if (mounted) setIsFreeToday(isFree);
      } catch (error) {
        console.warn('[BookReader] Failed to check daily free', error);
      } finally {
        if (mounted) setCheckingFree(false);
      }
    };
    checkFree();
    return () => {
      mounted = false;
    };
  }, [params.bookId, params.book, readerData?.book?.id, params.languageCode]);
  
  // Load reading settings
  useEffect(() => {
    getReadingSettings().then(setReadingSettings);
  }, []);
  
  // Load saved status
  useEffect(() => {
    if (!readerData || currentIndex < 0 || currentIndex >= readerData.segments.length || !auth.currentUser) return;
    
    const segment = readerData.segments[currentIndex];
    if (!segment || segment.type !== 'insight' || !segment.sourceInsight) return;
    
    const loadInsightData = async () => {
      try {
        const result = await PrivateProfileService.getProfile(auth.currentUser!.uid);
        if (result.success && result.data) {
          const insightId = segment.sourceInsight!.id;
          const insightProgress = result.data.insightsProgress?.[insightId];
          setIsSaved(insightProgress?.isSaved || false);
        }
      } catch (error) {
        console.error('Error loading insight data:', error);
      }
    };
    
    loadInsightData();
  }, [readerData, currentIndex]);
  
  // Reset hasLoadedAudio when navigating to a different book
  // This ensures audio is reloaded when changing books
  const bookIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentBookId = readerData?.book?.id;
    const isCollection = readerData?.isCollection;
    
    // Create a unique key for this content
    const contentKey = isCollection ? `collection-${readerData?.segments.length}` : currentBookId;
    
    if (bookIdRef.current && bookIdRef.current !== contentKey) {
      console.log('📚 Book/content changed, resetting audio flag');
      setHasLoadedAudio(false);
    }
    
    bookIdRef.current = contentKey;
  }, [readerData?.book?.id, readerData?.isCollection, readerData?.segments.length]);
  
  // Load audio
  // IMPORTANT: Audio playlist order MUST match page order
  // Page order: [Introduction?, Insight1, Insight2, ...]
  // Audio playlist: [intro_audio?, insight1_audio, insight2_audio, ...]
  // Index mapping: pageIndex === audioPlaylistIndex
  useEffect(() => {
    if (!readerData) return;
    
    const loadAudio = async () => {
      const allInsights = readerData.segments
        .filter(s => s.type === 'insight' && s.sourceInsight)
        .map(s => s.sourceInsight!);
      
      if (allInsights.length === 0) return;
      
      // Check if playlist is already loaded with the right content
      // This prevents unnecessary rebuilds when component updates but allows
      // loading when navigating to a different book
      if (hasLoadedAudio && playlist.length > 0) {
        const playlistInsightIds = playlist.filter(p => p.type === 'insight').map(p => p.id).sort();
        const currentInsightIds = allInsights.map(i => i.id).sort();
        const playlistMatches = playlistInsightIds.length === currentInsightIds.length &&
          playlistInsightIds.every((id, index) => id === currentInsightIds[index]);
        
        if (playlistMatches) {
          console.log('📚 Playlist already loaded with correct content, skipping');
          return;
        } else {
          console.log('📚 Playlist content changed (different book or insights), reloading audio');
        }
      }
      
      console.log('📚 BookReaderScreen: Loading audio for', readerData.segments.length, 'pages');
      console.log('📚 Pages:', readerData.segments.map((s, i) => `${i}:${s.type}`).join(', '));
      
      // Load audio in same order as pages
      // DON'T force rebuild - let the context decide if playlist needs rebuilding
      if (readerData.isCollection) {
        // Mixed insights - no intro
        await loadMixedInsights(allInsights, false); // Don't force rebuild
      } else if (readerData.book) {
        // Single book - may have intro
        const hasIntro = readerData.segments[0]?.type === 'intro';
        
        if (hasIntro) {
          // Use loadBookAudio which includes intro
          await loadBookAudio(readerData.book, allInsights);
        } else {
          // No intro, just load insights
          await loadInsightsOnly(allInsights, readerData.book, false, false); // Don't force rebuild
        }
      } else {
        await loadInsightsOnly(allInsights, undefined, false, false); // Don't force rebuild
      }
      
      setHasLoadedAudio(true);
      
      // Log final state after a moment
      setTimeout(() => {
        console.log('📚 ✅ Audio loaded! Playlist length:', playlist.length);
        if (playlist.length > 0) {
          console.log('📚 Playlist order:', playlist.map((p, i) => `${i}:${p.title.substring(0, 30)}`).join(' | '));
        }
      }, 200);
      
      // Auto-play audio if requested
      if (params.autoPlayAudio) {
        setTimeout(async () => {
          console.log('🎵 Auto-playing audio for page', currentIndex);
          await jumpToSegment(currentIndex);
          await playPause();
        }, 500);
      }
    };
    
    loadAudio();
  }, [readerData?.book?.id, readerData?.isCollection, readerData?.segments.length, hasLoadedAudio]);
  
  // Sync page with audio ONLY when audio auto-advances (not on manual play)
  // Track previous audio index to detect sequential auto-advance
  const prevAudioIndexRef = useRef<number>(-1);
  
  useEffect(() => {
    if (!readerData || playlist.length === 0 || audioCurrentIndex === null || audioCurrentIndex < 0) return;
    if (audioCurrentIndex >= readerData.segments.length) return;
    
    const prevAudioIndex = prevAudioIndexRef.current;
    
    // Only sync page when audio auto-advances sequentially (track finished and moved to next)
    // Don't sync when user manually jumps to a track (clicking play button)
    const isSequentialAdvance = prevAudioIndex >= 0 && audioCurrentIndex === prevAudioIndex + 1;
    
    if (isSequentialAdvance && audioCurrentIndex !== currentIndex && isPlaying) {
      console.log(`🔄 Audio auto-advanced: ${prevAudioIndex} → ${audioCurrentIndex}, syncing page`);
      flatListRef.current?.scrollTo({ x: audioCurrentIndex * SCREEN_WIDTH, y: 0, animated: true });
    }
    
    // Always update the ref to track the current audio index
    prevAudioIndexRef.current = audioCurrentIndex;
  }, [audioCurrentIndex, isPlaying]);
  
  // Show install prompt on web when content is locked
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    const locked = !hasContentAccess && !isFreeToday;
    if (locked && !isSubscriptionLoading && !checkingFree) {
      setShowInstallPrompt(true);
    } else {
      setShowInstallPrompt(false);
    }
  }, [hasContentAccess, isFreeToday, isSubscriptionLoading, checkingFree]);
  
  const handleNext = () => {
    if (!readerData || currentIndex >= readerData.segments.length - 1) return;
    // Manual navigation - don't auto-change audio
    flatListRef.current?.scrollTo({ x: (currentIndex + 1) * SCREEN_WIDTH, y: 0, animated: true });
  };
  
  const handlePrevious = () => {
    if (!readerData || currentIndex <= 0) return;
    // Manual navigation - don't auto-change audio
    flatListRef.current?.scrollTo({ x: (currentIndex - 1) * SCREEN_WIDTH, y: 0, animated: true });
  };
  
  const handleSegmentPress = (index: number) => {
    if (index === currentIndex) return;
    // Manual navigation - don't auto-change audio
    flatListRef.current?.scrollTo({ x: index * SCREEN_WIDTH, y: 0, animated: true });
  };
  
  const handleClose = () => {
    // Check if we can go back in the navigation stack
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // If no previous screen, navigate to home
      navigation.navigate('Home' as never);
    }
  };
  
  const handleSave = async () => {
    if (!auth.currentUser || !readerData?.segments[currentIndex]) return;
    
    const currentSegment = readerData.segments[currentIndex];
    if (currentSegment.type !== 'insight' || !currentSegment.sourceInsight) return;
    
    const insightId = currentSegment.sourceInsight.id;
    // For mixed insights, get bookId from segment; for single book, get from readerData
    const bookId = currentSegment.bookId || readerData.book?.id;
    
    if (!bookId) return;
    
    const newSavedState = !isSaved;
    setIsSaved(newSavedState);
    
    try {
      const result = await PrivateProfileService.toggleInsightSaved(
        auth.currentUser.uid,
        insightId,
        bookId
      );
      
      if (!result.success) {
        setIsSaved(!newSavedState);
      }
    } catch (error) {
      console.error('Error saving insight:', error);
      setIsSaved(!newSavedState);
    }
  };
  
  const handleBookPress = (bookId: string) => {
    // Extract base bookId (remove language code if present)
    const cleanBookId = bookId.includes('_') ? bookId.split('_')[0] : bookId;
    const languageCode = bookId.includes('_') ? bookId.split('_')[1] : 'en';
    
    // @ts-ignore - Navigation types
    navigation.navigate('BookDetail', {
      bookId: cleanBookId,
      languageCode,
    });
  };
  
  if (isLoading || !readerData) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Theme.colors.primary} />
      </View>
    );
  }
  
  const totalSegments = readerData.segments.length;
  const locked = !hasContentAccess && !isFreeToday;
  
  // Map reading font preferences to actual font families
  const fontFamily = 
    readingSettings?.fontFamily === 'poppins' ? 'Poppins-Regular'
    : readingSettings?.fontFamily === 'outfit' ? 'Outfit-Regular'
    : Platform.select({ ios: 'Times New Roman', android: 'serif', default: 'serif' });
  
  // Map font size preferences to actual sizes
  const fontSize = 
    readingSettings?.fontSize === 'small' ? 15
    : readingSettings?.fontSize === 'large' ? 19
    : readingSettings?.fontSize === 'huge' ? 24
    : 17; // medium (default)
  
  const renderPage = (seg: ReaderSegment, pageIndex: number) => {
    const isCurrentPage = pageIndex === currentIndex;
    
    // On web, only show first page as preview if content is locked
    const isContentBlocked = Platform.OS === 'web' && locked && pageIndex > 0;
    
    // Prepare widgets for this page
    const widgets: Array<{
      type: 'action' | 'benefit' | 'reflection';
      icon: string;
      title: string;
      items: string[];
    }> = [];
    
    if (seg.type === 'insight') {
      if (seg.actionSteps && seg.actionSteps.length > 0) {
        widgets.push({
          type: 'action',
          icon: '💡',
          title: 'Put It in Action',
          items: seg.actionSteps.map(step => 
            step.explanation ? `${step.directive}\n${step.explanation}` : step.directive
          ),
        });
      }
      if (seg.reflectionQuestions && seg.reflectionQuestions.length > 0) {
        widgets.push({
          type: 'reflection',
          icon: '💭',
          title: 'Reflect on This',
          items: seg.reflectionQuestions,
        });
      }
    }
    
    // If content is blocked on web, show locked overlay instead
    if (isContentBlocked) {
      return (
        <View style={styles.lockedPageContainer}>
          <View style={styles.lockedContent}>
            <Ionicons name="lock-closed" size={48} color={Theme.colors.primary} />
            <Text variant="h2" style={{ textAlign: 'center', marginTop: Theme.spacing.md, marginBottom: Theme.spacing.sm }}>
              Get the Full App
            </Text>
            <Text variant="body1" style={{ textAlign: 'center', color: Theme.colors.textSecondary, marginBottom: Theme.spacing.lg }}>
              Install the mobile app to access all book content and features
            </Text>
            <Button 
              title="Install App" 
              onPress={() => setShowInstallPrompt(true)}
            />
          </View>
        </View>
      );
    }
    
    return (
      <ScrollView 
        ref={isCurrentPage ? scrollViewRef : null}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
        scrollEnabled={true}
        nestedScrollEnabled={true}
        bounces={true}
      >
          {/* Title */}
          <View style={styles.titleSection}>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>
                {seg.type === 'intro' ? 'Introduction' : 'Key Insight'}
              </Text>
            </View>
            <Text style={styles.title}>{seg.title}</Text>
            {seg.subtitle && (
              <Text style={styles.subtitle}>{seg.subtitle}</Text>
            )}
          </View>

          {/* Audio Button */}
          <View style={styles.playButtonContainer}>
            <Pressable 
              style={styles.playButton} 
              onPress={async () => {
                console.log('🎵 Play button clicked on page', pageIndex, '(', seg.type, ')');
                console.log('🎵 Current audio index:', audioCurrentIndex, 'isPlaying:', isPlaying);
                console.log('🎵 Total playlist length:', playlist.length);
                
                // If not playing or playing different segment, jump to current page's audio
                if (!isPlaying || audioCurrentIndex !== pageIndex) {
                  console.log('🎵 Jumping to segment', pageIndex, '(audioCurrentIndex was:', audioCurrentIndex, ')');
                  await jumpToSegment(pageIndex);
                }
                await playPause();
                
                // Show the full audio player
                showFullPlayer();
              }}
            >
              <LinearGradient
                colors={seg.type === 'intro' ? ['#F59E0B', '#D97706'] : [Theme.colors.primary500, Theme.colors.primary600]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.playGradient}
              >
                <Ionicons 
                  name={isPlaying && audioCurrentIndex === pageIndex ? "pause" : "play"} 
                  size={24} 
                  color="#fff" 
                  style={!(isPlaying && audioCurrentIndex === pageIndex) && { marginLeft: 3 }}
                />
                <Text style={styles.playText}>
                  {isPlaying && audioCurrentIndex === pageIndex ? 'Pause Audio' : 'Play Audio'}
                </Text>
              </LinearGradient>
            </Pressable>
          </View>

          {/* Content */}
          <MarkdownRenderer 
            content={seg.contentText}
            fontSize={fontSize}
            fontFamily={fontFamily}
            lineHeight={fontSize * 1.6}
          />

          {/* About Author (intro only) */}
          {seg.type === 'intro' && seg.aboutAuthor && (
            <View style={styles.widgetCard}>
              <View style={styles.widgetHeader}>
                <RNText style={styles.widgetIcon}>✍️</RNText>
                <Text style={styles.widgetTitle}>About the Author</Text>
              </View>
              <MarkdownRenderer 
                content={seg.aboutAuthor}
                fontSize={fontSize}
                fontFamily={fontFamily}
                lineHeight={fontSize * 1.6}
              />
            </View>
          )}
          
          {/* Widgets */}
          {widgets.map((widget, index) => {
            const gradientColors: [string, string] = 
              widget.type === 'action' ? ['#eff6ff', '#ffffff']
              : widget.type === 'benefit' ? ['#f0fdf4', '#ffffff']
              : ['#f0fdf4', '#ffffff'];
            const borderColor = 
              widget.type === 'action' ? '#bfdbfe'
              : widget.type === 'benefit' ? '#bbf7d0'
              : '#bbf7d0';
            const iconBg = 
              widget.type === 'action' ? '#3b82f6'
              : widget.type === 'benefit' ? '#22c55e'
              : '#10b981';
            
            return (
              <View key={index} style={styles.widgetCardShadow}>
                <LinearGradient
                  colors={gradientColors}
                  style={[styles.widgetCard, { borderColor, borderWidth: 2 }]}
                >
                  <View style={styles.widgetHeader}>
                    <View style={styles.widgetIconCircle}>
                      <RNText style={styles.widgetIcon}>{widget.icon}</RNText>
                    </View>
                    <Text style={styles.widgetTitle}>{widget.title}</Text>
                  </View>
                  <View style={styles.widgetItems}>
                    {widget.items.map((item, i) => {
                      const lines = item.split('\n');
                      const firstLine = lines[0];
                      const restLines = lines.slice(1).join('\n');
                      
                      return (
                        <View key={i} style={styles.widgetItem}>
                          <View style={[styles.widgetNumber, { backgroundColor: iconBg }]}>
                            <Text style={styles.widgetNumberText}>{i + 1}</Text>
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text style={[styles.widgetItemText, { fontFamily: Theme.typography.fontFamily.semiBold, fontWeight: '600' }]}>
                              {firstLine}
                            </Text>
                            {restLines ? (
                              <Text style={[styles.widgetItemText, { marginTop: 4 }]}>
                                {restLines}
                              </Text>
                            ) : null}
                          </View>
                        </View>
                      );
                    })}
                  </View>
                </LinearGradient>
              </View>
            );
          })}

          {/* Engagement Buttons */}
          {seg.type === 'insight' && isCurrentPage && (
            <View style={styles.engagementSection}>
              <Pressable
                style={[styles.engagementButton, isSaved && styles.engagementButtonSaved]}
                onPress={handleSave}
              >
                <Ionicons 
                  name={isSaved ? "heart" : "heart-outline"} 
                  size={20} 
                  color={isSaved ? "#fff" : Theme.colors.textPrimary} 
                />
                <Text style={StyleSheet.flatten([styles.engagementText, isSaved && { color: '#fff' }])}>
                  {isSaved ? 'Saved' : 'Save'}
                </Text>
              </Pressable>
              
              <Pressable style={styles.engagementButton}>
                <Ionicons name="create-outline" size={20} color={Theme.colors.textPrimary} />
                <Text style={styles.engagementText}>Note</Text>
              </Pressable>
            </View>
          )}
          
          {/* Book Reference */}
          {seg.bookId && (
            <View style={styles.bookReferenceShadow}>
              <Pressable onPress={() => handleBookPress(seg.bookId!)} style={{ borderRadius: styles.bookReference.borderRadius, overflow: 'hidden' }}>
                <LinearGradient
                  colors={['#fffbeb', '#ffffff']}
                  style={styles.bookReference}
                >
                  {seg.bookCoverUrl ? (
                    <View style={styles.bookCoverShadow}>
                      <Image 
                        source={{ uri: seg.bookCoverUrl }} 
                        style={styles.bookCover}
                        resizeMode="cover"
                      />
                    </View>
                  ) : (
                    <View style={[styles.bookCoverShadow, { backgroundColor: '#e5e7eb' }]} />
                  )}
                  <View style={styles.bookInfo}>
                    <Text style={styles.bookLabel}>From the book</Text>
                    <Text style={styles.bookTitle}>{seg.bookTitle}</Text>
                    {readerData.book?.author && (
                      <Text style={styles.bookAuthor}>{readerData.book.author}</Text>
                    )}
                  </View>
                  <Ionicons 
                    name="chevron-forward" 
                    size={20} 
                    color={Theme.colors.textSecondary} 
                    style={{ marginLeft: 'auto' }}
                  />
                </LinearGradient>
              </Pressable>
            </View>
          )}

          <View style={{ height: 120 }} />
        </ScrollView>
    );
  };
  
  return (
    <View style={styles.container}>
      {/* Header with Progress */}
      <View style={styles.header}>
        <Pressable style={styles.headerButton} onPress={handleClose}>
          <Ionicons name="close" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        
        <View style={styles.progressContainer}>
          {readerData.segments.map((seg, index) => {
            const isActive = index === currentIndex;
            const segmentColor = seg.type === 'intro' ? '#F59E0B' : '#3B82F6';
            
            return (
              <Pressable
                key={index}
                style={[
                  styles.progressSegment,
                  isActive && styles.progressSegmentActive,
                ]}
                onPress={() => handleSegmentPress(index)}
              >
                <View
                  style={[
                    styles.progressSegmentFill,
                    { backgroundColor: segmentColor },
                    isActive && styles.progressSegmentFillActive,
                  ]}
                />
              </Pressable>
            );
          })}
        </View>
        
        <Pressable style={styles.headerButton} onPress={() => setIsSettingsVisible(true)}>
          <Ionicons name="text" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
      </View>

      {/* Fixed Navigation Arrows */}
      <View style={styles.navArrows} pointerEvents="box-none">
        <Pressable 
          style={[styles.navArrow, styles.navArrowLeft, currentIndex === 0 && styles.navArrowHidden]}
          onPress={handlePrevious}
          disabled={currentIndex === 0}
        >
          {currentIndex > 0 && (
            <Ionicons name="chevron-back" size={24} color={Theme.colors.textPrimary} />
          )}
        </Pressable>
        
        <Pressable 
          style={[styles.navArrow, styles.navArrowRight, currentIndex >= totalSegments - 1 && styles.navArrowHidden]}
          onPress={handleNext}
          disabled={currentIndex >= totalSegments - 1}
        >
          {currentIndex < totalSegments - 1 && (
            <Ionicons name="chevron-forward" size={24} color={Theme.colors.textPrimary} />
          )}
        </Pressable>
      </View>

      {/* Horizontal Paging with ScrollView */}
      <ScrollView
        ref={flatListRef as any}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        decelerationRate="fast"
        snapToInterval={SCREEN_WIDTH}
        snapToAlignment="start"
        onScroll={(e) => {
          const offsetX = e.nativeEvent.contentOffset.x;
          const newIndex = Math.round(offsetX / SCREEN_WIDTH);
          if (newIndex !== currentIndex && newIndex >= 0 && newIndex < readerData.segments.length) {
            setCurrentIndex(newIndex);
            scrollViewRef.current?.scrollTo({ y: 0, animated: false });
          }
        }}
        style={styles.flatListContainer}
        nestedScrollEnabled={true}
      >
        {readerData.segments.map((seg, index) => (
          <View key={`${seg.id}-${index}`} style={styles.pageContainer}>
            {renderPage(seg, index)}
          </View>
        ))}
      </ScrollView>

      {isFullPlayerVisible && <FullAudioPlayer showCloseButton={true} />}

      <ReadingSettingsSheet
        visible={isSettingsVisible}
        onClose={() => setIsSettingsVisible(false)}
        onApply={(s) => setReadingSettings(s)}
      />

      {locked && !isSubscriptionLoading && !checkingFree && Platform.OS !== 'web' && (
        <View style={styles.lockOverlay} pointerEvents="box-none">
          <View style={styles.lockBackdrop} />
          <View style={styles.lockCard}>
            <Text variant="h2" style={{ textAlign: 'center', marginBottom: Theme.spacing.sm }}>
              Upgrade to continue
            </Text>
            <Text variant="body2" color="secondary" style={{ textAlign: 'center', marginBottom: Theme.spacing.md }}>
              Subscribe to read and listen. Daily free books stay fully unlocked.
            </Text>
            <Button title="Upgrade" onPress={() => navigation.navigate('Paywall' as never)} />
          </View>
        </View>
      )}
      {Platform.OS === 'web' && (
        <InstallAppPrompt
          visible={showInstallPrompt}
          onDismiss={() => setShowInstallPrompt(false)}
        />
      )}
    </View>
  );
};

export default BookReaderScreen;
